import sharedDatasInstance from "../sharedDatas";
import { useMutation, useQuery } from "@1f/react-sdk";
import { apiErrorHelper } from "../helpers/apiErrorHelpers";
import {
    query_cerved_searchEntities,
    query_cerved_entityProfile,
    query_cerved_entityContacts,
    query_cerved_entityConnections,
    query_cerved_entityBeneficialOwners,
} from "../query/cervedQueries";
import { mutation_cerved_searchEntitiesAdvanced } from "../mutations/cervedMutations";

export const CervedApi = () => {
    const tenantId = sharedDatasInstance.getPropertyByName("tenantId");
    const aziendaId = sharedDatasInstance.getPropertyByName("aziendaId");
    const errorApiHelpers = apiErrorHelper();

    const apiSearchEntities = useQuery(
        query_cerved_searchEntities,
        {},
        { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
    );
    const apiEntityProfile = useQuery(
        query_cerved_entityProfile,
        {},
        { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
    );
    const apiEntityContacts = useQuery(
        query_cerved_entityContacts,
        {},
        { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
    );
    const apiEntityConnections = useQuery(
        query_cerved_entityConnections,
        {},
        { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
    );
    const apiEntityBeneficialOwners = useQuery(
        query_cerved_entityBeneficialOwners,
        {},
        { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
    );
    const apiSearchEntitiesAdvanced = useMutation(mutation_cerved_searchEntitiesAdvanced, {
        graphqlEndpoint: "aml:hasura:api://v1/graphql",
    });

    // 503 = feature Cerved disabilitata → soft-fail (come Workspace): ritorna null
    const isFeatureDisabled = (error) =>
        errorApiHelpers.retriveErrorStatusCode(error) === 503;

    // Ricerca testuale semplice (opzionale rispetto alla ricerca avanzata PARS-1402)
    const SearchEntities = async ({ searchText, entityType, excludeCeased } = {}) => {
        try {
            const response = await apiSearchEntities.fetch({
                tenantId,
                aziendaId,
                searchText: searchText ?? null,
                entityType: entityType ?? null,
                excludeCeased: excludeCeased ?? null,
            });
            return response?.data?.data?.cervedSearchEntities ?? null;
        } catch (error) {
            if (isFeatureDisabled(error)) return null;
            const errorMessage = errorApiHelpers.handleApiError(error);
            throw new Error(errorMessage || "Errore durante la ricerca Cerved");
        }
    };

    // Ricerca avanzata (PARS-1402): criteri valorizzati in AND, max 50 risultati
    const SearchEntitiesAdvanced = async (criteria) => {
        try {
            const response = await apiSearchEntitiesAdvanced.fetch({
                tenantId,
                aziendaId,
                criteria,
            });
            return response?.data?.data?.cervedSearchEntitiesAdvanced ?? null;
        } catch (error) {
            if (isFeatureDisabled(error)) return null;
            const errorMessage = errorApiHelpers.handleApiError(error);
            throw new Error(errorMessage || "Errore durante la ricerca avanzata Cerved");
        }
    };

    // Visura: denominazione, P.IVA/CF, sede legale, ATECO, REA, natura giuridica
    const GetProfile = async (idSoggetto) => {
        try {
            const response = await apiEntityProfile.fetch({ tenantId, aziendaId, idSoggetto });
            return response?.data?.data?.cervedEntityProfile ?? null;
        } catch (error) {
            if (isFeatureDisabled(error)) return null;
            const errorMessage = errorApiHelpers.handleApiError(error);
            throw new Error(errorMessage || "Errore durante il recupero della visura Cerved");
        }
    };

    // Contatti (PEC/email/telefono)
    const GetContacts = async (idSoggetto) => {
        try {
            const response = await apiEntityContacts.fetch({ tenantId, aziendaId, idSoggetto });
            return response?.data?.data?.cervedEntityContacts ?? null;
        } catch (error) {
            if (isFeatureDisabled(error)) return null;
            const errorMessage = errorApiHelpers.handleApiError(error);
            throw new Error(errorMessage || "Errore durante il recupero dei contatti Cerved");
        }
    };

    // Cariche/collegamenti → legale rappresentante (PG/DI)
    const GetConnections = async (idSoggetto, includeTerminated = false) => {
        try {
            const response = await apiEntityConnections.fetch({
                tenantId,
                aziendaId,
                idSoggetto,
                includeTerminated,
            });
            return response?.data?.data?.cervedEntityConnections ?? null;
        } catch (error) {
            if (isFeatureDisabled(error)) return null;
            const errorMessage = errorApiHelpers.handleApiError(error);
            throw new Error(errorMessage || "Errore durante il recupero dei collegamenti Cerved");
        }
    };

    // Titolari effettivi → generazione anagrafiche collegate
    const GetBeneficialOwners = async (idSoggetto, shareValue = null) => {
        try {
            const response = await apiEntityBeneficialOwners.fetch({
                tenantId,
                aziendaId,
                idSoggetto,
                shareValue,
            });
            return response?.data?.data?.cervedEntityBeneficialOwners ?? null;
        } catch (error) {
            if (isFeatureDisabled(error)) return null;
            const errorMessage = errorApiHelpers.handleApiError(error);
            throw new Error(errorMessage || "Errore durante il recupero dei titolari effettivi Cerved");
        }
    };

    return {
        SearchEntities,
        SearchEntitiesAdvanced,
        GetProfile,
        GetContacts,
        GetConnections,
        GetBeneficialOwners,
    };
};
